import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotateCw } from "lucide-react";

const MissionFilter = ({ missions, onFilter }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    client: "",
    employee: "",
    startDate: "",
    endDate: "",
    month: ""
  });

  // Clients uniques triés
  const uniqueClients = useMemo(() => {
    return [...new Set(missions.map((m) => m.client).filter(Boolean))].sort();
  }, [missions]);

  // Employés uniques (supporte string ou tableau)
  const uniqueEmployees = useMemo(() => {
    const all = missions.flatMap((m) =>
      Array.isArray(m.employees)
        ? m.employees.map((e) => e.employee_name)
        : [m.employee]
    );
    return [...new Set(all.filter(Boolean))].sort();
  }, [missions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = { ...filters, [name]: value };
    if (name === "month") {
      updatedFilters.startDate = "";
      updatedFilters.endDate = "";
    }
    setFilters(updatedFilters);
    applyFilters(updatedFilters);
  };

  const resetFilters = () => {
    const reset = {
      search: "",
      type: "",
      client: "",
      employee: "",
      startDate: "",
      endDate: "",
      month: ""
    };
    setFilters(reset);
    applyFilters(reset);
  };

  const applyFilters = (filterSet) => {
    let start = filterSet.startDate;
    let end = filterSet.endDate;
    if (filterSet.month) {
      const [year, month] = filterSet.month.split("-");
      const paddedMonth = month.padStart(2, "0");
      start = `${year}-${paddedMonth}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      end = `${year}-${paddedMonth}-${String(lastDay).padStart(2, "0")}`;
    }
    const filtered = missions.filter((m) => {
      const s = (filterSet.search || "").toLowerCase();
      const searchMatch = !s || (
        m.client?.toLowerCase().includes(s) ||
        m.type?.toLowerCase().includes(s) ||
        m.address?.toLowerCase().includes(s) ||
        String(m.id).includes(s) ||
        (Array.isArray(m.employees)
          ? m.employees.some((e) => e.employee_name?.toLowerCase().includes(s))
          : m.employee?.toLowerCase().includes(s))
      );
      const typeMatch = filterSet.type ? m.type === filterSet.type : true;
      const clientMatch = filterSet.client ? m.client === filterSet.client : true;
      const employeeMatch = filterSet.employee
        ? (Array.isArray(m.employees)
            ? m.employees.some((e) => e.employee_name === filterSet.employee)
            : m.employee === filterSet.employee)
        : true;
      const dateStr = m.date;
      const startMatch = start ? dateStr >= start : true;
      const endMatch = end ? dateStr <= end : true;
      return searchMatch && typeMatch && clientMatch && employeeMatch && startMatch && endMatch;
    });
    onFilter(filtered);
  };

  return (
    <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm p-4 flex flex-wrap gap-3 mb-6 items-end">
      {/* Recherche texte */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">Recherche</label>
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40 text-xs pointer-events-none">🔍</span>
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleChange}
            placeholder={t("mission.filters.searchPlaceholder")}
            className="input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none pl-7 min-w-[180px]"
          />
        </div>
      </div>

      {/* Type */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">
          {t("mission.filters.type")}
        </label>
        <select
          name="type"
          value={filters.type}
          onChange={handleChange}
          className="select select-bordered select-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
        >
          <option value="">{t("mission.filters.allTypes")}</option>
          <option value="Moving">🚚 {t("mission.filters.moving")}</option>
          <option value="Delivery">📦 {t("mission.filters.delivery")}</option>
        </select>
      </div>

      {/* Client */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">
          {t("mission.filters.client")}
        </label>
        <select
          name="client"
          value={filters.client}
          onChange={handleChange}
          className="select select-bordered select-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
        >
          <option value="">{t("mission.filters.allClients")}</option>
          {uniqueClients.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Employé */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">
          {t("mission.filters.employee")}
        </label>
        <select
          name="employee"
          value={filters.employee}
          onChange={handleChange}
          className="select select-bordered select-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
        >
          <option value="">{t("mission.filters.allEmployees")}</option>
          {uniqueEmployees.map((e) => (
            <option key={e}>{e}</option>
          ))}
        </select>
      </div>

      {/* Mois */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">
          {t("mission.filters.month")}
        </label>
        <input
          type="month"
          name="month"
          value={filters.month}
          onChange={handleChange}
          className="input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
        />
      </div>

      {/* Date début */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">
          {t("mission.filters.startDate")}
        </label>
        <input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleChange}
          className="input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          placeholder={t("mission.filters.startDate")}
        />
      </div>

      {/* Date fin */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-base-content/70">
          {t("mission.filters.endDate")}
        </label>
        <input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleChange}
          className="input input-bordered input-sm bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          placeholder={t("mission.filters.endDate")}
        />
      </div>

      {/* Bouton reset */}
      <button
        onClick={resetFilters}
        className="btn btn-sm btn-ghost text-base-content/50 flex items-center gap-2"
      >
        <RotateCw size={14} /> {t("buttons.reset")}
      </button>
    </div>
  );
};

export default MissionFilter;
